import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Character } from '../types/character';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import EquipmentIcon from './EquipmentIcon';
import './CharacterList.css';

interface CharacterListProps {
  onCreateCharacter: () => void;
  onEditCharacter: (character: Character) => void;
  onViewProgression?: (character: Character) => void;
  onManageEquipment?: (character: Character) => void;
  refreshTrigger: number;
}

interface EquippedItem {
  id: number;
  itemName: string;
  itemType: number;
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  mpBonus: number;
  speedBonus: number;
  durability: number;
  maxDurability: number;
  imageUrl?: string;
  iconName?: string;
}

interface CharacterEquipment {
  weapon?: EquippedItem;
  armor?: EquippedItem;
  accessory?: EquippedItem;
}

// XP calculation functions (matching backend formula: 100 * level^1.5)
const getExperienceRequiredForLevel = (level: number): number => {
  if (level <= 1) return 0;
  return Math.floor(100 * Math.pow(level, 1.5));
};

const getTotalExperienceForLevel = (level: number): number => {
  let totalXP = 0;
  for (let i = 2; i <= level; i++) {
    totalXP += getExperienceRequiredForLevel(i);
  }
  return totalXP;
};

const CharacterList: React.FC<CharacterListProps> = ({
  onCreateCharacter,
  onEditCharacter,
  onViewProgression,
  onManageEquipment,
  refreshTrigger
}) => {
  const { token, handleAuthError } = useAuth();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [characterEquipment, setCharacterEquipment] = useState<Map<number, CharacterEquipment>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const fetchAllCharacterEquipment = async (characters: Character[]) => {
    const equipmentMap = new Map<number, CharacterEquipment>();
    
    // Fetch equipment for each character in parallel
    const equipmentPromises = characters.map(async (character) => {
      try {
        const response = await makeAuthenticatedRequest(`/equipment/character/${character.id}`);
        if (response.ok) {
          const equipment = await response.json();
          equipmentMap.set(character.id, equipment);
        }
      } catch (error) {
        console.warn(`Failed to fetch equipment for character ${character.id}:`, error);
        // Don't fail the whole operation for equipment fetch failures
      }
    });

    await Promise.allSettled(equipmentPromises);
    setCharacterEquipment(equipmentMap);
  };

  const fetchCharacters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest('/characters');

      if (!response.ok) {
        throw new Error('Failed to fetch characters');
      }

      const data = await response.json();
      setCharacters(data);
      
      // Fetch equipment for each character
      await fetchAllCharacterEquipment(data);
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    fetchCharacters();
  }, [token, refreshTrigger, fetchCharacters]);

  const handleDeleteCharacter = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) {
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`/characters/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete character');
      }

      setCharacters(prev => prev.filter(char => char.id !== id));
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    }
  };

  const getHealthPercentage = (current: number, max: number) => {
    return Math.round((current / max) * 100);
  };

  const getHealthColor = (percentage: number) => {
    if (percentage >= 70) return '#4CAF50';
    if (percentage >= 40) return '#FF9800';
    return '#F44336';
  };

  if (loading) {
    return <div className="character-list-loading">Loading characters...</div>;
  }

  if (error) {
    return <div className="character-list-error">Error: {error}</div>;
  }

  return (
    <div className="character-list">
      <div className="character-list-header">
        <h2>Your Characters ({characters.length}/10)</h2>
        <button 
          onClick={onCreateCharacter}
          disabled={characters.length >= 10}
          className="create-character-button"
        >
          Create New Character
        </button>
      </div>

      {characters.length === 0 ? (
        <div className="no-characters">
          <p>You haven't created any characters yet.</p>
          <button onClick={onCreateCharacter} className="create-first-character">
            Create Your First Character
          </button>
        </div>
      ) : (
        <div className="character-grid">
          {characters.map(character => (
            <div key={character.id} className="character-card">
              <div className="character-header">
                {(character.talentPoints ?? 0) > 0 && (
                  <div className="talent-points-badge" title="Unspent Talent Points - Visit Skills!">
                    ⭐ {character.talentPoints}
                  </div>
                )}
                <div className="character-title-row">
                  <h3>{character.name}</h3>
                  <span className="level">Level {character.level}</span>
                  <span className="race-class">{character.race} {character.class}</span>
                </div>
              </div>

              {/* XP Progress Bar */}
              <div className="xp-progress">
                <div className="xp-bar-container">
                  <div
                    className="xp-bar-fill"
                    style={{
                      width: `${Math.min(100, (character.experience / getTotalExperienceForLevel(character.level + 1)) * 100)}%`
                    }}
                  />
                </div>
                <span className="xp-text">{character.experience} / {getTotalExperienceForLevel(character.level + 1)} XP</span>
              </div>

              <div className="character-stats">
                <div className="health-mana">
                  <div className="stat-bar">
                    <label>HP</label>
                    <div className="bar-container">
                      <div 
                        className="bar-fill" 
                        style={{
                          width: `${getHealthPercentage(character.currentHP, character.maxHP)}%`,
                          backgroundColor: getHealthColor(getHealthPercentage(character.currentHP, character.maxHP))
                        }}
                      />
                    </div>
                    <span className="bar-text">{character.currentHP}/{character.maxHP}</span>
                  </div>
                  
                  <div className="stat-bar">
                    <div className="bar-container">
                      <div 
                        className="bar-fill" 
                        style={{
                          width: `${getHealthPercentage(character.currentMP, character.maxMP)}%`,
                          backgroundColor: '#2196F3'
                        }}
                      />
                    </div>
                    <span className="bar-text">{character.currentMP}/{character.maxMP}</span>
                  </div>
                </div>

                <div className="primary-stats">
                  <div className="stat-row">
                    <span>STR: {character.strength}</span>
                    <span>DEX: {character.dexterity}</span>
                    <span>VIT: {character.vitality}</span>
                  </div>
                  <div className="stat-row">
                    <span>INT: {character.intelligence}</span>
                    <span>WIS: {character.wisdom}</span>
                    <span>CON: {character.constitution}</span>
                  </div>
                </div>

                <div className="equipment-display">
                  {(() => {
                    const equipment = characterEquipment.get(character.id);
                    const totalBonuses = equipment ? {
                      attack: (equipment.weapon?.attackBonus || 0) + (equipment.armor?.attackBonus || 0) + (equipment.accessory?.attackBonus || 0),
                      defense: (equipment.weapon?.defenseBonus || 0) + (equipment.armor?.defenseBonus || 0) + (equipment.accessory?.defenseBonus || 0),
                      hp: (equipment.weapon?.hpBonus || 0) + (equipment.armor?.hpBonus || 0) + (equipment.accessory?.hpBonus || 0),
                      mp: (equipment.weapon?.mpBonus || 0) + (equipment.armor?.mpBonus || 0) + (equipment.accessory?.mpBonus || 0),
                      speed: (equipment.weapon?.speedBonus || 0) + (equipment.armor?.speedBonus || 0) + (equipment.accessory?.speedBonus || 0),
                    } : { attack: 0, defense: 0, hp: 0, mp: 0, speed: 0 };

                    return (
                      <>
                        <div className="equipment-slots">
                          <div className="equipment-slot-container">
                            <div className="slot-label">Weapon</div>
                            {equipment?.weapon ? (
                              <EquipmentIcon
                                imageUrl={equipment.weapon.imageUrl}
                                iconName={equipment.weapon.iconName}
                                itemName={equipment.weapon.itemName}
                                itemType="Weapon"
                                size="medium"
                                showTooltip={true}
                                stats={{
                                  attackBonus: equipment.weapon.attackBonus,
                                  defenseBonus: equipment.weapon.defenseBonus,
                                  hpBonus: equipment.weapon.hpBonus,
                                  mpBonus: equipment.weapon.mpBonus,
                                  speedBonus: equipment.weapon.speedBonus
                                }}
                              />
                            ) : (
                              <div className="equipment-slot empty">
                                <span className="empty-text">⚔️</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="equipment-slot-container">
                            <div className="slot-label">Armor</div>
                            {equipment?.armor ? (
                              <EquipmentIcon
                                imageUrl={equipment.armor.imageUrl}
                                iconName={equipment.armor.iconName}
                                itemName={equipment.armor.itemName}
                                itemType="Armor"
                                size="medium"
                                showTooltip={true}
                                stats={{
                                  attackBonus: equipment.armor.attackBonus,
                                  defenseBonus: equipment.armor.defenseBonus,
                                  hpBonus: equipment.armor.hpBonus,
                                  mpBonus: equipment.armor.mpBonus,
                                  speedBonus: equipment.armor.speedBonus
                                }}
                              />
                            ) : (
                              <div className="equipment-slot empty">
                                <span className="empty-text">🛡️</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="equipment-slot-container">
                            <div className="slot-label">Accessory</div>
                            {equipment?.accessory ? (
                              <EquipmentIcon
                                imageUrl={equipment.accessory.imageUrl}
                                iconName={equipment.accessory.iconName}
                                itemName={equipment.accessory.itemName}
                                itemType="Accessory"
                                size="medium"
                                showTooltip={true}
                                stats={{
                                  attackBonus: equipment.accessory.attackBonus,
                                  defenseBonus: equipment.accessory.defenseBonus,
                                  hpBonus: equipment.accessory.hpBonus,
                                  mpBonus: equipment.accessory.mpBonus,
                                  speedBonus: equipment.accessory.speedBonus
                                }}
                              />
                            ) : (
                              <div className="equipment-slot empty">
                                <span className="empty-text">💍</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {(totalBonuses.attack > 0 || totalBonuses.defense > 0 || totalBonuses.hp > 0 || totalBonuses.mp > 0 || totalBonuses.speed > 0) && (
                          <div className="equipment-bonuses">
                            {totalBonuses.attack > 0 && <span className="bonus positive">ATK +{totalBonuses.attack}</span>}
                            {totalBonuses.defense > 0 && <span className="bonus positive">DEF +{totalBonuses.defense}</span>}
                            {totalBonuses.hp > 0 && <span className="bonus positive">HP +{totalBonuses.hp}</span>}
                            {totalBonuses.speed > 0 && <span className="bonus positive">SPD +{totalBonuses.speed}</span>}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="combat-stats">
                  <span>AP: {character.actionPoints}</span>
                  <span>XP: {character.experience}</span>
                </div>
              </div>

              <div className="character-actions">
                <button
                  onClick={() => onEditCharacter(character)}
                  className="edit-button"
                >
                  Edit
                </button>
                {onViewProgression && (
                  <button
                    onClick={() => onViewProgression(character)}
                    className="progression-button"
                  >
                    Skills
                  </button>
                )}
                {onManageEquipment && (
                  <button
                    onClick={() => onManageEquipment(character)}
                    className="equipment-button"
                  >
                    Equip
                  </button>
                )}
                <button
                  onClick={() => handleDeleteCharacter(character.id, character.name)}
                  className="delete-button"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CharacterList;